import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Grid, Tab, Tabs } from '@mui/material';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { SCUILoadingLinear } from '../../atomic/atoms';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUIContent } from '../../atomic/organisms';
import { PageUrl } from '../../types/enums/pageUrl-enums';
import { AutoReturnAndResendNotFoundPanel } from './components/AutoReturnAndResendNotFoundPanel';
import { AutoReturnAndResendProcessedPanel } from './components/AutoReturnAndResendProcessedPanel';
import { AutoReturnAndResendReportDetailDrawer } from './components/AutoReturnAndResendReportDetailDrawer';
import { AutoReturnAndResendReportsPanel } from './components/AutoReturnAndResendReportsPanel';
import { AutoReturnAndResendResendPanel } from './components/AutoReturnAndResendResendPanel';
import { AutoReturnAndResendAutoReturnPanel } from './components/AutoReturnAndResendAutoReturnPanel';
import { AutoReturnAndResendSearchArea } from './components/AutoReturnAndResendSearchArea';
import { AutoReturnAndResendSummaryCards } from './components/AutoReturnAndResendSummaryCards';
import { ARAR } from './autoReturnAndResend.classes';
import {
    buildProcessedRow,
    filterShippingInput,
    getCurrentOperatorUsername,
    MAX_SHIPMENT_COUNT,
    normalizeAutoReturnReports,
    parseShippingNumbers,
    resolveInitialActiveTab,
} from './autoReturnAndResend.helpers';
import AutoReturnAndResendCommand from '../../services/command/autoReturnAndResend/AutoReturnAndResendCommand.services';
import AutoReturnAndResendQuery from '../../services/query/autoReturnAndResend/AutoReturnAndResendQuery.services';
import {
    AutoReturnAndResendTabStatus,
    type AutoReturnAndResendItemDto,
    type ProcessedRow,
    type AutoReturnReport,
    type AutoReturnAndResendMainTab,
    type AutoReturnAndResendTab,
    type ShipmentRow,
} from './autoReturnAndResend.types';

export function AutoReturnAndResend() {
    const { t } = useTranslation();
    const [mainTab, setMainTab] = useState<AutoReturnAndResendMainTab>('operation');
    const [shippingInput, setShippingInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [activeTab, setActiveTab] = useState<AutoReturnAndResendTab>('resend');
    const [resendRows, setResendRows] = useState<ShipmentRow[]>([]);
    const [autoReturnRows, setAutoReturnRows] = useState<ShipmentRow[]>([]);
    const [processedRows, setProcessedRows] = useState<ProcessedRow[]>([]);
    const [notFoundRows, setNotFoundRows] = useState<ShipmentRow[]>([]);
    const [reports, setReports] = useState<AutoReturnReport[]>([]);
    const [selectedReport, setSelectedReport] = useState<AutoReturnReport | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const shipmentNumbers = useMemo(() => parseShippingNumbers(shippingInput), [shippingInput]);
    const isOverLimit = shipmentNumbers.length > MAX_SHIPMENT_COUNT;
    const currentUsername = useMemo(() => getCurrentOperatorUsername(), []);

    const loadReports = useCallback(async () => {
        setLoading(true);

        try {
            const result = await AutoReturnAndResendQuery.GetAutoReturnReports();
            setReports(normalizeAutoReturnReports(result));
        } catch (error) {
            console.error('Error during GetAutoReturnReports:', error);
            setReports([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadReports();
    }, [loadReports]);

    const handleMainTabChange = useCallback((_: unknown, value: AutoReturnAndResendMainTab) => {
        setMainTab(value);

        if (value === 'reports') {
            void loadReports();
        }

        if (value !== 'reports') {
            setDrawerOpen(false);
            setSelectedReport(null);
        }
    }, [loadReports]);

    const handleInputChange = useCallback((value: string) => {
        setShippingInput(filterShippingInput(value));
    }, []);

    const handleClear = useCallback(() => {
        setShippingInput('');
        setHasSearched(false);
        setActiveTab('resend');
        setResendRows([]);
        setAutoReturnRows([]);
        setProcessedRows([]);
        setNotFoundRows([]);
    }, []);

    const handleSearch = useCallback(async () => {
        if (shipmentNumbers.length === 0 || isOverLimit) {
            return;
        }

        setLoading(true);

        try {
            const result = await AutoReturnAndResendCommand.CheckAutoReturnAndResendStatus(shipmentNumbers);
            const items = result?.items ?? [];
            const toShipmentRow = (item: AutoReturnAndResendItemDto): ShipmentRow => ({
                shippingListNo: item.shippingListNo,
                shippingListId: item.shippingListId,
                orderNumber: item.orderNumber,
            });
            const resendRows = items
                .filter((item) => item.tabStatus === AutoReturnAndResendTabStatus.ReShipping)
                .map(toShipmentRow);
            const autoReturnRows = items
                .filter((item) => item.tabStatus === AutoReturnAndResendTabStatus.AutoReturnAvailable)
                .map(toShipmentRow);
            const notFoundRows = items
                .filter((item) => item.tabStatus === AutoReturnAndResendTabStatus.Unavailable)
                .map(toShipmentRow);
            const processedRows = items
                .filter((item) => item.tabStatus === AutoReturnAndResendTabStatus.Processing)
                .map((item) => buildProcessedRow(toShipmentRow(item), currentUsername, 'processing'));

            setResendRows(resendRows);
            setAutoReturnRows(autoReturnRows);
            setNotFoundRows(notFoundRows);
            setProcessedRows(processedRows);
            setActiveTab(
                resolveInitialActiveTab(
                    resendRows.length,
                    autoReturnRows.length,
                    notFoundRows.length,
                    processedRows.length
                )
            );
            setHasSearched(true);
        } catch (error) {
            console.error('Error during CheckAutoReturnAndResendStatus:', error);
        } finally {
            setLoading(false);
        }
    }, [currentUsername, isOverLimit, shipmentNumbers]);

    const moveRowsToProcessed = useCallback(
        async (rowsToProcess: ShipmentRow[]) => {
            if (rowsToProcess.length === 0) {
                return false;
            }

            setLoading(true);

            try {
                const targetShippingListNumbers = rowsToProcess.map((row) => row.shippingListNo);
                const result = await AutoReturnAndResendCommand.ProcessAutoReturnBulk(targetShippingListNumbers);

                if (result && result.rpaRequestId) {
                    const queuedShippingListNumbers =
                        result.queuedShippingListNumbers?.length > 0
                            ? new Set(result.queuedShippingListNumbers)
                            : new Set(targetShippingListNumbers);
                    const queuedRows = rowsToProcess.filter((row) =>
                        queuedShippingListNumbers.has(row.shippingListNo)
                    );
                    const queuedRowsSet = new Set(queuedRows.map((row) => row.shippingListNo));

                    setAutoReturnRows((prev) => prev.filter((row) => !queuedRowsSet.has(row.shippingListNo)));
                    setProcessedRows((prev) => [
                        ...queuedRows.map((row) => buildProcessedRow(row, currentUsername)),
                        ...prev,
                    ]);

                    await Swal.fire({
                        icon: 'success',
                        title: t('autoReturnAndResend.autoReturn.queuedTitle', 'İşlem Kuyruğa Alındı'),
                        html:
                            `<div style="text-align: left; font-size: 14px; line-height: 1.8;">` +
                            `<div><b>Talep No (RpaRequestId):</b> ${result.rpaRequestId}</div>` +
                            `<div><b>Kuyruğa Alınan:</b> ${result.queuedCount} adet</div>` +
                            (result.skippedCount > 0
                                ? `<div style="color: #d32f2f;"><b>Atlanan (Zaten İşlemde):</b> ${result.skippedCount} adet</div>`
                                : '') +
                            `</div>`,
                        confirmButtonText: t('general.ok', 'Tamam'),
                    });

                    setActiveTab('processed');
                    return true;
                }
                return false;
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Bir hata oluştu';
                await Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: errorMessage,
                });
                return false;
            } finally {
                setLoading(false);
            }
        },
        [currentUsername, t]
    );

    const handleSendAutoReturn = useCallback(
        (rowsToProcess: ShipmentRow[]) => {
            moveRowsToProcessed(rowsToProcess);
        },
        [moveRowsToProcessed]
    );

    const handleBulkStartAutoReturn = useCallback(async () => {
        if (autoReturnRows.length === 0) {
            return;
        }

        const rowsToSend = [...autoReturnRows];
        const wasQueued = await moveRowsToProcessed(rowsToSend);
        if (wasQueued) {
            if (mainTab === 'reports') {
                await loadReports();
            }
        }
    }, [loadReports, mainTab, moveRowsToProcessed, autoReturnRows]);

    const handleViewReportDetails = useCallback((report: AutoReturnReport) => {
        setSelectedReport(report);
        setDrawerOpen(true);
    }, []);

    const handleCloseReportDrawer = useCallback(() => {
        setDrawerOpen(false);
        setSelectedReport(null);
    }, []);

    const renderActivePanel = () => {
        switch (activeTab) {
            case 'resend':
                return <AutoReturnAndResendResendPanel rows={resendRows} onLoadingChange={setLoading} />;
            case 'autoReturn':
                return (
                    <AutoReturnAndResendAutoReturnPanel
                        rows={autoReturnRows}
                        onSendAutoReturn={handleSendAutoReturn}
                        onBulkStartAutoReturn={handleBulkStartAutoReturn}
                    />
                );
            case 'processed':
                return <AutoReturnAndResendProcessedPanel rows={processedRows} />;
            case 'notFound':
                return <AutoReturnAndResendNotFoundPanel rows={notFoundRows} />;
            default:
                return null;
        }
    };

    return (
        <SCUIContent className={ARAR.page}>
            <Grid container spacing={2}>
                <Grid className={ARAR.breadcrumbSection} size={12}>
                    <SCUIBreadCrumb
                        title={t('autoReturnAndResend.pageTitle')}
                        componentLink={PageUrl.autoReturnAndResend.url}
                        style={{ cursor: 'pointer' }}
                    />
                </Grid>

                <Grid className={ARAR.mainTabsSection} size={12}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                            value={mainTab}
                            onChange={handleMainTabChange}
                            indicatorColor="primary"
                            textColor="primary"
                            sx={{
                                '& .MuiTab-root': {
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    color: 'grey.700',
                                    textTransform: 'none',
                                    minHeight: 48,
                                    px: 3,
                                    opacity: 1,
                                },
                                '& .MuiTab-root.Mui-selected': {
                                    color: 'primary.main',
                                    fontWeight: 700,
                                },
                                '& .MuiTabs-indicator': {
                                    height: 3,
                                },
                            }}
                        >
                            <Tab value="operation" label={t('autoReturnAndResend.mainTabs.operation')} />
                            <Tab value="reports" label={t('autoReturnAndResend.mainTabs.reports')} />
                        </Tabs>
                    </Box>
                </Grid>

                <Grid className={ARAR.loadingSection} size={12}>
                    {loading && <SCUILoadingLinear display={loading} />}
                </Grid>

                {mainTab === 'operation' && (
                    <>
                        <Grid size={12}>
                            <AutoReturnAndResendSearchArea
                                shippingInput={shippingInput}
                                shipmentCount={shipmentNumbers.length}
                                isOverLimit={isOverLimit}
                                onInputChange={handleInputChange}
                                onClear={handleClear}
                                onSearch={handleSearch}
                            />
                        </Grid>

                        {hasSearched && (
                            <>
                                <Grid size={12}>
                                    <AutoReturnAndResendSummaryCards
                                        activeTab={activeTab}
                                        resendCount={resendRows.length}
                                        autoReturnCount={autoReturnRows.length}
                                        processedCount={processedRows.length}
                                        notFoundCount={notFoundRows.length}
                                        onTabChange={setActiveTab}
                                    />
                                </Grid>
                                <Grid size={12}>{renderActivePanel()}</Grid>
                            </>
                        )}
                    </>
                )}

                {mainTab === 'reports' && (
                    <Grid size={12}>
                        <AutoReturnAndResendReportsPanel reports={reports} onViewDetails={handleViewReportDetails} />
                    </Grid>
                )}
            </Grid>

            <AutoReturnAndResendReportDetailDrawer
                open={drawerOpen}
                report={selectedReport}
                onClose={handleCloseReportDrawer}
            />
        </SCUIContent>
    );
}
